const field = (path, label, type = 'string', opts = {}) => ({ path, label, type, ...opts });

const req = (path, label, type = 'string', opts = {}) => field(path, label, type, { required: true, ...opts });

export const TABS = [
  {
    id: 'case',
    key: 'case_bundle',
    label: 'Case',
    blurb: 'The denial as it arrived. This is the document the pipeline will read.',
    sections: [
      {
        id: 'identity',
        label: 'Identity',
        fields: [
          req('schema_version', 'Schema version'),
          req('bundle_type', 'Bundle type', 'string', { readOnly: true }),
          req('case_id', 'Case id'),
          req('case_category', 'Category'),
          req('case_created_at', 'Created at'),
          req('analysis_as_of', 'Analysis as of'),
          req('data_classification', 'Data classification'),
          req('input_completeness', 'Input completeness'),
        ],
      },
      {
        id: 'patient',
        label: 'Patient',
        fields: [
          req('patient.patient_id', 'Patient id'),
          field('patient.external_patient_id', 'External patient id', 'string', { nullable: true }),
          field('patient.date_of_birth', 'Date of birth', 'string', { nullable: true }),
          field('patient.sex', 'Sex', 'string', { nullable: true }),
          field('patient.name', 'Name', 'string', { nullable: true }),
          req('patient.deidentified', 'De-identified', 'bool'),
        ],
      },
      {
        id: 'insurance',
        label: 'Insurance',
        fields: [
          req('insurance.payer_id', 'Payer id'),
          req('insurance.payer_name', 'Payer name'),
          req('insurance.payer_type', 'Payer type'),
          req('insurance.plan.plan_id', 'Plan id'),
          req('insurance.plan.plan_name', 'Plan name'),
          req('insurance.plan.plan_type', 'Plan type'),
          req('insurance.plan.effective_from', 'Plan effective from'),
          field('insurance.plan.effective_to', 'Plan effective to', 'string', { nullable: true }),
          req('insurance.plan.member_id', 'Member id'),
          field('insurance.plan.group_number', 'Group number', 'string', { nullable: true }),
          req('insurance.plan.in_network', 'In network', 'bool'),
          req('insurance.plan.eligibility_status', 'Eligibility status'),
        ],
      },
      {
        id: 'claim',
        label: 'Claim',
        fields: [
          req('claim.claim_id', 'Claim id'),
          req('claim.claim_type', 'Claim type'),
          req('claim.claim_form', 'Claim form'),
          field('claim.original_payer_claim_control_number', 'Payer claim control number', 'string', { nullable: true }),
          req('claim.claim_frequency_code', 'Frequency code'),
          req('claim.date_of_service_start', 'Date of service start'),
          req('claim.date_of_service_end', 'Date of service end'),
          req('claim.billing_provider.provider_id', 'Billing provider id'),
          field('claim.billing_provider.npi', 'Billing NPI', 'string', { nullable: true }),
          field('claim.billing_provider.name', 'Billing provider name', 'string', { nullable: true }),
          field('claim.billing_provider.taxonomy', 'Billing taxonomy', 'string', { nullable: true }),
          req('claim.billing_provider.network_status', 'Billing network status'),
          req('claim.rendering_provider.provider_id', 'Rendering provider id'),
          field('claim.rendering_provider.npi', 'Rendering NPI', 'string', { nullable: true }),
          field('claim.rendering_provider.name', 'Rendering provider name', 'string', { nullable: true }),
          field('claim.rendering_provider.taxonomy', 'Rendering taxonomy', 'string', { nullable: true }),
          req('claim.ordering_provider.provider_id', 'Ordering provider id'),
          field('claim.ordering_provider.npi', 'Ordering NPI', 'string', { nullable: true }),
          field('claim.ordering_provider.name', 'Ordering provider name', 'string', { nullable: true }),
          req('claim.ordering_provider.identifier_status', 'Ordering identifier status'),
          req('claim.service_facility.facility_id', 'Facility id'),
          field('claim.service_facility.name', 'Facility name', 'string', { nullable: true }),
          req('claim.service_facility.place_of_service', 'Facility place of service'),
        ],
      },
      {
        id: 'diagnoses',
        label: 'Diagnoses',
        arrayPath: 'claim.diagnoses',
        itemLabel: 'Diagnosis',
        fields: [
          req('diagnosis_id', 'Diagnosis id'),
          req('sequence', 'Sequence', 'number'),
          req('code_system', 'Code system'),
          field('code', 'Code', 'string', { nullable: true }),
          req('description', 'Description'),
          req('is_principal', 'Principal', 'bool'),
          field('evidence_source_ids', 'Evidence source ids', 'strings'),
          req('coding_status', 'Coding status'),
        ],
      },
      {
        id: 'lines',
        label: 'Claim lines',
        arrayPath: 'claim.lines',
        itemLabel: 'Line',
        fields: [
          req('line_id', 'Line id'),
          req('line_number', 'Line number', 'number'),
          req('service_date', 'Service date'),
          req('procedure.code_system', 'Procedure code system'),
          req('procedure.code', 'Procedure code'),
          field('procedure.modifiers', 'Modifiers', 'strings'),
          field('procedure.description', 'Procedure description', 'string', { nullable: true }),
          req('place_of_service', 'Place of service'),
          req('units', 'Units', 'number'),
          field('diagnosis_pointers', 'Diagnosis pointers', 'strings'),
          req('billed_amount', 'Billed amount', 'number'),
          req('contract_allowed_amount', 'Contract allowed', 'number'),
          req('payer_paid_initial', 'Payer paid', 'number'),
          req('patient_responsibility_initial', 'Patient responsibility', 'number'),
          req('denial_status', 'Denial status'),
          field('order_ids', 'Order ids', 'strings'),
          field('clinical_source_ids', 'Clinical source ids', 'strings'),
          field('notes', 'Notes', 'strings'),
        ],
      },
      {
        id: 'adjustments',
        label: 'Line adjustments',
        arrayPath: 'claim.lines',
        itemLabel: 'Line',
        nestedArray: 'adjustments',
        nestedLabel: 'Adjustment',
        fields: [
          req('group_code', 'CAS group'),
          req('carc', 'CARC'),
          field('rarc', 'RARC'),
          req('adjustment_amount', 'Amount', 'number', { alias: 'amount' }),
        ],
      },
      {
        id: 'clinical',
        label: 'Clinical context',
        fields: [
          field('clinical_context.encounter.encounter_id', 'Encounter id', 'string', { nullable: true }),
          req('clinical_context.encounter.care_setting', 'Care setting'),
          field('clinical_context.encounter.admission_time', 'Admission time', 'string', { nullable: true }),
          field('clinical_context.encounter.discharge_time', 'Discharge time', 'string', { nullable: true }),
          req('clinical_context.encounter.clinical_summary', 'Clinical summary', 'text'),
        ],
      },
      {
        id: 'orders',
        label: 'Orders',
        arrayPath: 'clinical_context.orders',
        itemLabel: 'Order',
        fields: [
          req('order_id', 'Order id'),
          req('order_date_time', 'Ordered at'),
          req('ordered_service', 'Ordered service'),
          field('reason_for_order', 'Reasons', 'strings'),
          req('status', 'Status'),
          req('ordering_provider_id', 'Ordering provider id'),
        ],
      },
      {
        id: 'results',
        label: 'Results',
        arrayPath: 'clinical_context.results',
        itemLabel: 'Result',
        fields: [
          req('report_id', 'Report id'),
          req('report_type', 'Report type'),
          req('status', 'Status'),
          field('result_date_time', 'Result time', 'string', { nullable: true }),
          req('summary', 'Summary', 'text'),
          field('related_order_ids', 'Related order ids', 'strings'),
        ],
      },
      {
        id: 'performed',
        label: 'Performed services',
        arrayPath: 'clinical_context.performed_services',
        itemLabel: 'Service',
        fields: [
          req('service_id', 'Service id'),
          req('description', 'Description'),
          req('performance_status', 'Performance status'),
          req('source_id', 'Source id'),
        ],
      },
      {
        id: 'notes',
        label: 'Notes',
        arrayPath: 'clinical_context.notes',
        itemLabel: 'Note',
        fields: [
          req('note_id', 'Note id'),
          req('summary', 'Summary', 'text'),
        ],
      },
      {
        id: 'authorization',
        label: 'Authorization',
        fields: [
          field('authorization.authorization_id', 'Authorization id', 'string', { nullable: true }),
          req('authorization.status', 'Status'),
          field('authorization.prior_authorization_required', 'Prior auth required', 'string', { nullable: true }),
          req('authorization.exception_claimed', 'Exception claimed', 'bool'),
          field('authorization.supporting_source_ids', 'Supporting source ids', 'strings'),
        ],
      },
      {
        id: 'status',
        label: 'Claim status',
        fields: [
          req('claim_status.overall_status', 'Overall status'),
          req('claim_status.received_denial_date', 'Denial received'),
          req('claim_status.appeal_deadline', 'Appeal deadline'),
          field('claim_status.corrected_claim_deadline', 'Corrected-claim deadline', 'string', { nullable: true }),
        ],
      },
      {
        id: 'status-events',
        label: 'Status events',
        arrayPath: 'claim_status.status_events',
        itemLabel: 'Event',
        fields: [
          req('event_id', 'Event id'),
          req('date', 'Date'),
          req('event_type', 'Event type'),
          req('description', 'Description', 'text'),
          req('source_id', 'Source id'),
        ],
      },
      {
        id: 'financials',
        label: 'Financials',
        fields: [
          req('financials.currency', 'Currency'),
          req('financials.total_billed', 'Total billed', 'number'),
          req('financials.initial_payer_paid', 'Initial payer paid', 'number'),
          req('financials.initial_patient_responsibility', 'Initial patient responsibility', 'number'),
          field('financials.estimated_additional_collectible', 'Estimated additional collectible', 'number', { nullable: true }),
          field('financials.confirmed_additional_paid', 'Confirmed additional paid', 'number', { nullable: true }),
          req('financials.financial_estimate_status', 'Estimate status'),
          req('financials.excluded_related_payment_amount', 'Excluded related payment', 'number'),
          field('financials.calculation_notes', 'Calculation notes', 'strings'),
        ],
      },
      {
        id: 'documents',
        label: 'Source documents',
        arrayPath: 'reference_documents.documents',
        itemLabel: 'Document',
        fields: [
          req('source_id', 'Source id'),
          req('source_type', 'Source type'),
          req('title', 'Title'),
          req('origin', 'Origin'),
          req('content_format', 'Content format'),
          field('document_date', 'Document date', 'string', { nullable: true }),
          req('verified', 'Verified', 'bool'),
          req('verification_status', 'Verification status'),
        ],
      },
      {
        id: 'lineage',
        label: 'Source lineage',
        arrayPath: 'source_lineage',
        itemLabel: 'Source',
        fields: [
          req('source_id', 'Source id'),
          req('source_format', 'Format'),
          req('source_file', 'File'),
          req('role', 'Role'),
          req('available_at_inference', 'Available at inference', 'bool'),
        ],
      },
      {
        id: 'processing',
        label: 'Processing',
        fields: [
          req('processing_context.requested_operation', 'Requested operation'),
          field('processing_context.target_line_ids', 'Target line ids', 'strings'),
          req('processing_context.human_review_required', 'Human review required', 'bool'),
          req('processing_context.external_actions_allowed', 'External actions allowed', 'bool'),
          field('processing_context.fixture_policy_warning', 'Fixture warning', 'text'),
          field('processing_context.known_data_gaps', 'Known data gaps', 'strings'),
        ],
      },
    ],
  },
  {
    id: 'reference',
    key: 'claim_reference_bundle',
    label: 'Reference',
    blurb: 'Reference material the system pulls for this case before resolution.',
    sections: [
      {
        id: 'ref-identity',
        label: 'Identity',
        fields: [
          req('schema_version', 'Schema version'),
          req('bundle_type', 'Bundle type', 'string', { readOnly: true }),
          req('case_id', 'Case id'),
          req('reference_bundle_id', 'Reference bundle id'),
          req('case_bundle_version', 'Case bundle version'),
          req('build_status', 'Build status'),
          field('build_timestamp', 'Build timestamp', 'string', { nullable: true }),
        ],
      },
      {
        id: 'basis',
        label: 'Selection basis',
        fields: [
          req('selection_basis.payer_id', 'Payer id'),
          req('selection_basis.plan_id', 'Plan id'),
          req('selection_basis.date_of_service', 'Date of service'),
          req('selection_basis.claim_type', 'Claim type'),
          field('selection_basis.denial_codes', 'Denial codes', 'strings'),
        ],
      },
      {
        id: 'sets',
        label: 'Reference sets',
        arrayPath: 'reference_sets',
        itemLabel: 'Reference set',
        fields: [
          req('reference_id', 'Reference id'),
          req('reference_type', 'Reference type'),
          field('graph_id', 'Graph id', 'string', { nullable: true }),
          field('version', 'Version', 'string', { nullable: true }),
          field('effective_from', 'Effective from', 'string', { nullable: true }),
          field('effective_to', 'Effective to', 'string', { nullable: true }),
          field('applicability_status', 'Applicability', 'string', { nullable: true }),
          field('provenance_source_id', 'Provenance source id', 'string', { nullable: true }),
          field('loaded_content', 'Loaded content', 'string', { nullable: true }),
        ],
      },
      {
        id: 'tasks',
        label: 'Strategy tasks',
        arrayPath: 'strategy_tasks',
        itemLabel: 'Task',
        fields: [
          req('task_id', 'Task id'),
          req('claim_line_id', 'Claim line id'),
          req('activation_reason.carc', 'CARC'),
          field('activation_reason.rarc_codes', 'RARC codes', 'strings'),
          field('strategy_ids', 'Strategy ids', 'strings'),
          field('process_map_ids', 'Process map ids', 'strings'),
          req('execution_status', 'Execution status'),
          field('source_ids', 'Source ids', 'strings'),
          field('requested_checks', 'Requested checks', 'strings'),
        ],
      },
      {
        id: 'rules',
        label: 'Cross-source rules',
        arrayPath: 'cross_source_rules',
        itemLabel: 'Rule',
        fields: [
          req('rule_id', 'Rule id'),
          req('description', 'Description', 'text'),
          req('status', 'Status'),
        ],
      },
      {
        id: 'gates',
        label: 'Gates and gaps',
        fields: [
          field('gating_process_map_ids', 'Gating process map ids', 'strings'),
          field('unresolved_references', 'Unresolved references', 'strings'),
        ],
      },
    ],
  },
  {
    id: 'resolution',
    key: 'case_resolution_bundle',
    label: 'Expected resolution',
    blurb: 'The answer key. A later pipeline run will be compared with this document. Editing it does not run the pipeline.',
    sections: [
      {
        id: 'res-identity',
        label: 'Identity',
        fields: [
          req('schema_version', 'Schema version'),
          req('bundle_type', 'Bundle type', 'string', { readOnly: true }),
          req('case_id', 'Case id'),
          req('resolution_id', 'Resolution id'),
          req('reference_bundle_id', 'Reference bundle id'),
          field('generated_at', 'Generated at', 'string', { nullable: true }),
          field('engine_version', 'Engine version', 'string', { nullable: true }),
          field('run_id', 'Run id', 'string', { nullable: true }),
          req('analysis_status', 'Analysis status'),
          req('final_disposition', 'Final disposition'),
          req('final_reason', 'Final reason', 'text'),
          req('execution_status', 'Execution status'),
        ],
      },
      {
        id: 'line-resolutions',
        label: 'Line resolutions',
        arrayPath: 'line_resolutions',
        itemLabel: 'Line resolution',
        fields: [
          req('line_id', 'Line id'),
          req('disposition', 'Disposition'),
          req('reasoning_summary', 'Reasoning', 'text'),
          field('root_cause_codes', 'Root cause codes', 'strings'),
          req('clinical_necessity', 'Clinical necessity'),
          req('service_performed', 'Service performed'),
          req('coding_compliance', 'Coding compliance'),
          req('coverage_applicability', 'Coverage'),
          req('estimated_additional_collectible', 'Estimated collectible', 'number'),
          req('estimate_basis', 'Estimate basis'),
          req('confidence', 'Confidence'),
          req('human_approval_required', 'Human approval required', 'bool'),
          field('strategy_execution_ids', 'Strategy execution ids', 'strings'),
          field('missing_information', 'Missing information', 'strings'),
        ],
      },
      {
        id: 'gates-out',
        label: 'Claim gates',
        arrayPath: 'claim_level_gates',
        itemLabel: 'Gate',
        fields: [
          req('gate_id', 'Gate id'),
          req('status', 'Status'),
          field('explanation', 'Explanation', 'text', { nullable: true }),
          field('supporting_source_ids', 'Supporting source ids', 'strings'),
        ],
      },
      {
        id: 'actions',
        label: 'Recommended actions',
        arrayPath: 'recommended_actions',
        itemLabel: 'Action',
        fields: [
          field('action_id', 'Action id', 'string', { nullable: true }),
          field('line_id', 'Line id', 'string', { nullable: true }),
          req('action_type', 'Action type'),
          req('execution_status', 'Execution status'),
          field('assigned_to', 'Assigned to', 'string', { nullable: true }),
          field('approved_by', 'Approved by', 'string', { nullable: true }),
          field('approved_at', 'Approved at', 'string', { nullable: true }),
          field('submitted_at', 'Submitted at', 'string', { nullable: true }),
          field('submission_channel', 'Submission channel', 'string', { nullable: true }),
          field('payer_tracking_id', 'Payer tracking id', 'string', { nullable: true }),
        ],
      },
      {
        id: 'money',
        label: 'Financial outcome',
        fields: [
          req('financial_outcome.currency', 'Currency'),
          req('financial_outcome.original_payer_paid', 'Original payer paid', 'number'),
          req('financial_outcome.estimated_additional_collectible', 'Estimated additional collectible', 'number'),
          field('financial_outcome.confirmed_additional_paid', 'Confirmed additional paid', 'number', { nullable: true }),
          field('financial_outcome.estimated_total_final_paid', 'Estimated total final paid', 'number', { nullable: true }),
          req('financial_outcome.excluded_related_payment_amount', 'Excluded related payment', 'number'),
          req('financial_outcome.recovery_confidence', 'Recovery confidence'),
          field('financial_outcome.actual_payment_reference_ids', 'Payment reference ids', 'strings'),
        ],
      },
      {
        id: 'audit',
        label: 'Audit',
        fields: [
          field('overall_evidence_chain', 'Evidence chain ids', 'strings'),
          field('unresolved_issues', 'Unresolved issues', 'strings'),
          field('warnings', 'Warnings', 'strings'),
        ],
      },
      {
        id: 'audit-events',
        label: 'Audit trail',
        arrayPath: 'audit_trail',
        itemLabel: 'Event',
        fields: [
          field('event_id', 'Event id', 'string', { nullable: true }),
          field('event_time', 'Event time', 'string', { nullable: true }),
          field('actor_type', 'Actor type', 'string', { nullable: true }),
          field('action', 'Action', 'string', { nullable: true }),
          field('description', 'Description', 'text', { nullable: true }),
        ],
      },
    ],
  },
];

export function getPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((cur, key) => (cur == null ? undefined : cur[key]), obj);
}

export function readField(obj, field) {
  const value = getPath(obj, field.path);
  const empty = value === undefined || value === null || value === '';
  if (empty && field.alias) {
    const alt = getPath(obj, field.alias);
    if (alt !== undefined && alt !== null && alt !== '') return alt;
  }
  return value;
}

export function setPath(obj, path, value) {
  const clone = structuredClone(obj);
  const parts = path.split('.');
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (cur[parts[i]] == null || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
}

function missing(fieldDef, value) {
  if (!fieldDef.required || fieldDef.nullable) return false;
  if (fieldDef.type === 'bool') return value === undefined || value === null;
  if (fieldDef.type === 'number') return value === undefined || value === null || value === '';
  if (fieldDef.type === 'strings') return false;
  return value === undefined || value === null || String(value).trim() === '';
}

export function validateDocument(tab, document) {
  const errors = {};
  tab.sections.forEach((section) => {
    if (!section.arrayPath) {
      section.fields.forEach((item) => {
        if (missing(item, readField(document, item))) errors[item.path] = 'Required';
      });
      return;
    }
    const rows = getPath(document, section.arrayPath) || [];
    if (section.nestedArray) {
      rows.forEach((row, rowIndex) => {
        const nested = row?.[section.nestedArray] || [];
        nested.forEach((child, childIndex) => {
          section.fields.forEach((item) => {
            if (missing(item, readField(child, item))) {
              errors[`${section.arrayPath}.${rowIndex}.${section.nestedArray}.${childIndex}.${item.path}`] = 'Required';
            }
          });
        });
      });
      return;
    }
    rows.forEach((row, rowIndex) => {
      section.fields.forEach((item) => {
        if (missing(item, readField(row, item))) {
          errors[`${section.arrayPath}.${rowIndex}.${item.path}`] = 'Required';
        }
      });
    });
  });
  return errors;
}

export function validateAll(bundles) {
  const byTab = {};
  let count = 0;
  TABS.forEach((tab) => {
    const errors = validateDocument(tab, bundles[tab.key] || {});
    byTab[tab.id] = errors;
    count += Object.keys(errors).length;
  });
  return { byTab, count };
}

export function syncIds(bundles) {
  const next = structuredClone(bundles);
  const caseId = next.case_bundle?.case_id;
  const refId = next.claim_reference_bundle?.reference_bundle_id;
  if (caseId) {
    next.claim_reference_bundle.case_id = caseId;
    next.case_resolution_bundle.case_id = caseId;
  }
  if (refId) next.case_resolution_bundle.reference_bundle_id = refId;
  return next;
}

export function money(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}
